import { forwardRef, ReactNode, useEffect, useRef, useState } from 'react';
import noScroll from 'no-scroll';
import { v1 as uuid } from 'uuid';
import { CSSTransition } from 'react-transition-group';
import cx from 'classnames';
import modalClasses from './modal.module.css';
import deviceBasedClasses from 'components/ModalBox/styles/index.module.scss';
import classes from 'components/ModalBox/styles/index.module.scss';
import { createPortal } from 'react-dom';
import { isNullOrUndefined, isUndefined } from '../../predicates/type';
import { modalManager } from './modalManager';
import { ModalBoxSizeType, ModalRenderProps } from './types';
import { _executeOnExitedCallbackAndCleanUp, modalBoxUtils } from './utils';
import { FocusTrap } from '../FocusTrap/index';
import { isMobileAny } from '../../utils/device';
import { useCallbackRef } from '../../hooks/useCallbackRef';

export type ModalProps = {
	onExited?: () => void;
	onClose?: () => void;
	size?: ModalBoxSizeType;
	open: boolean;
	hasFullScreen?: boolean;
	initialFullScreen?: boolean;
	hasDynamicContent?: boolean;
	hasAutoFocus?: boolean;
	// TODO: need to deprecate prop
	wizard?: boolean;
	render: (props: ModalRenderProps) => ReactNode;
	classNames?: {
		transitionEnter?: string;
		transitionEnterActive?: string;
		transitionExit?: string;
		transitionExitActive?: string;
	};
};

function unblockScroll() {
	if (modalManager.modals().length === 0) {
		noScroll.off();
	}
}

function blockScroll() {
	noScroll.on();
}

function onCloseMock() {}

const ANIMATION_DURATION = 300;

export const Modal = forwardRef<HTMLDivElement, ModalProps>(
	(
		{
			open,
			onClose,
			onExited,
			classNames,
			size = 'small',
			hasDynamicContent,
			render,
			hasAutoFocus = true,
			hasFullScreen = true,
			initialFullScreen,
			wizard,
		},
		ref,
	) => {
		const nodeRef = useRef(null);
		const isMountedContentRef = useRef(false);
		const [fullScreen, setFullScreen] = useState(initialFullScreen || false);
		const previousActiveElement = useRef<HTMLElement | null>(null);
		const focusPreviousActiveElement = useCallbackRef(() => {
			if (previousActiveElement.current) {
				previousActiveElement.current.focus();
			}
		});
		const portalRef = useRef<HTMLDivElement>();
		const modalIdRef = useRef(uuid() as string);
		const [showPortal, setShowPortal] = useState(false);
		const handleEntered = useCallbackRef(() => {
			modalBoxUtils.addClassForBody();
		});
		const handleExited = useCallbackRef(() => {
			if (!isNullOrUndefined(onExited)) {
				onExited();
			}

			modalBoxUtils.removeClassForBody();
			setShowPortal(false);
			unblockScroll();

			_executeOnExitedCallbackAndCleanUp();
		});
		const toggleFullScreen = useCallbackRef(() => {
			setFullScreen((prevState) => {
				return !prevState;
			});
		});
		const modalClassNames = cx('react-responsive-modal', modalClasses.modal, deviceBasedClasses.modal, {
			[deviceBasedClasses.modal_normalScreen]: !isMobileAny && !fullScreen,
			[deviceBasedClasses.modal_fullScreen]: !isMobileAny && fullScreen,
			[deviceBasedClasses.modal_tiny]: !isMobileAny && size === 'tiny',
			[deviceBasedClasses.modal_small]: !isMobileAny && size === 'small',
			[deviceBasedClasses.modal_medium]: !isMobileAny && size === 'medium',
			[deviceBasedClasses.modal_large]: !isMobileAny && size === 'large',
			[deviceBasedClasses.modal_extraLarge]: !isMobileAny && size === 'extraLarge',
			[classes.modal_wizard]: isMobileAny && wizard,
		});
		const handleKeydown = useCallbackRef((event: KeyboardEvent): void => {
			// Only the last modal need to be escaped when pressing the esc key
			if (event.keyCode !== 27 || !modalManager.isTopModal(modalIdRef.current)) {
				return;
			}

			onClose && onClose();
		});
		const removePortal = useCallbackRef(() => {
			if (!isUndefined(portalRef.current)) {
				document.body.removeChild(portalRef.current);
			}
		});
		const handleOpen = useCallbackRef(() => {
			isMountedContentRef.current = true;
			portalRef.current = document.createElement('div');
			document.body.appendChild(portalRef.current);
			modalManager.add(modalIdRef.current);
			blockScroll();
			document.addEventListener('keydown', handleKeydown);
			setShowPortal(true);
		});
		const handleClose = useCallbackRef(() => {
			if (!isMountedContentRef.current) {
				return;
			}

			isMountedContentRef.current = false;
			unblockScroll();
			modalManager.remove(modalIdRef.current);
			document.removeEventListener('keydown', handleKeydown);
		});

		useEffect(() => {
			if (!isMountedContentRef.current && open) {
				handleOpen();
			}
		}, [handleOpen, open, showPortal]);

		useEffect(() => {
			if (isMountedContentRef.current && !open) {
				handleClose();
			}
		}, [handleClose, open, removePortal]);

		useEffect(
			() => () => {
				handleClose();
				unblockScroll();
				removePortal();
			},
			[handleClose, removePortal],
		);

		useEffect(() => {
			if (open) {
				previousActiveElement.current = document.activeElement as HTMLElement;
			}

			return () => {
				if (open) {
					focusPreviousActiveElement();
					previousActiveElement.current = null;
				}
			};
		}, [open, focusPreviousActiveElement]);

		/*
			There are some cases when Modal being unmounted and there is no chance
			to execute CSSTransition onExited callback
			So it would be beneficial after some time on umount stage to check
			whether should we apply onExited callback or not
		*/
		useEffect(
			() => () => {
				setTimeout(() => {
					if (modalBoxUtils.isOpen()) {
						handleExited();
					}
				}, 300);
			},
			[handleExited],
		);

		if (!showPortal || isUndefined(portalRef.current)) {
			return null;
		}

		const execRender = () =>
			render({
				fullScreen: isMobileAny ? false : fullScreen,
				hasFullScreen: isMobileAny ? false : hasFullScreen,
				open,
				onClose: isUndefined(onClose) ? onCloseMock : onClose,
				toggleFullScreen,
				wizard,
			});

		const content = (
			<CSSTransition
				in={open}
				appear
				nodeRef={nodeRef}
				classNames={{
					appear: classNames?.transitionEnter || modalClasses.transitionEnter,
					appearActive: classNames?.transitionEnterActive || modalClasses.transitionEnterActive,
					enter: classNames?.transitionEnter || modalClasses.transitionEnter,
					enterActive: classNames?.transitionEnterActive || modalClasses.transitionEnterActive,
					exit: classNames?.transitionExit || modalClasses.transitionExit,
					exitActive: classNames?.transitionExitActive || modalClasses.transitionExitActive,
				}}
				timeout={ANIMATION_DURATION}
				onEntered={handleEntered}
				onExited={handleExited}
			>
				<div
					className={modalClasses.overlay}
					ref={ref}
				>
					<div
						className={modalClassNames}
						role='dialog'
						aria-modal='true'
					>
						{isMobileAny && execRender()}
						{!isMobileAny && (
							<FocusTrap
								initialFocusTrapState={open}
								hasDynamicContent={hasDynamicContent}
								hasAutoFocus={hasAutoFocus}
							>
								{execRender()}
							</FocusTrap>
						)}
					</div>
				</div>
			</CSSTransition>
		);

		return createPortal(content, portalRef.current);
	},
);
